import type { NavItem, NavSection } from "./nav-config.js";
import type { SidebarPreferences } from "@l30/shared";

/**
 * Pure reducer: nav config + saved prefs + current path → the sidebar's
 * render model. No hooks, no DOM — the unit-test seam.
 *
 * - Favourites resolve saved ids → items in pin order; unknown ids dropped.
 *   Duplicate ids in the stored JSONB are silently deduped (Set preserves
 *   first-seen / pin order).
 * - A section is collapsed iff it is collapsible AND saved-collapsed AND
 *   does not contain the active route (active-route force-expand is an
 *   override only — never written back to prefs).
 *
 * Active-match is segment-safe (see `isActiveHref`).
 *
 * Spec: docs/superpowers/specs/2026-06-07-sidebar-favourites-collapsible-design.md
 */
// Internal types — the reducer's return shape is consumed structurally
// (via inference) by the sidebar; these names are not imported elsewhere,
// so they stay unexported to satisfy the no-unused-exports gate (knip).
interface ResolvedSection {
  id: string;
  label: string;
  collapsible: boolean;
  items: NavItem[];
  collapsed: boolean;
}

interface ResolvedSidebar {
  favouriteItems: NavItem[];
  favouriteIds: Set<string>;
  sections: ResolvedSection[];
}

/**
 * Segment-safe active-route check.
 *
 * - Exact match always wins.
 * - `/dashboard` is never a prefix match (it would shadow everything).
 * - For all other hrefs, a child route like `/contacts/123` matches `/contacts`,
 *   but `/email-marketing` does NOT match `/email` — only a `/`-delimited
 *   child is considered active.
 *
 * Note: `/ideation/maps` matches both `/ideation` and `/ideation/maps`; that
 * is intentional — both items live in the same section and the section
 * force-expand for either is correct behaviour.
 */
export function isActiveHref(href: string, currentPath: string): boolean {
  if (currentPath === href) return true;
  if (href === "/dashboard") return false;
  return currentPath.startsWith(href + "/");
}

export function resolveSidebarState(
  sections: NavSection[],
  prefs: SidebarPreferences,
  currentPath: string,
): ResolvedSidebar {
  const itemById = new Map<string, NavItem>();
  for (const s of sections) for (const i of s.items) itemById.set(i.id, i);

  // Build the deduped id set first (Set preserves insertion / pin order;
  // stale JSONB with duplicate ids is silently collapsed here).
  const favouriteIds = new Set(prefs.favourites.filter((id) => itemById.has(id)));
  // Then derive items from the deduped set so duplicates never reach the render.
  const favouriteItems = [...favouriteIds].map((id) => itemById.get(id) as NavItem);

  const collapsedSet = new Set(prefs.collapsedSections);

  const resolved: ResolvedSection[] = sections.map((s) => {
    const containsActive = s.items.some((i) => isActiveHref(i.href, currentPath));
    const collapsed = s.collapsible && collapsedSet.has(s.id) && !containsActive;
    return { id: s.id, label: s.label, collapsible: s.collapsible, items: s.items, collapsed };
  });

  return { favouriteItems, favouriteIds, sections: resolved };
}
